import React, { useState, useRef, useCallback } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Modal, Backdrop, Fade, Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";

/* [CF 2026-08-17 G2] Client: "the system allows the location to be confirmed
   without clearly showing where the location is selected on the map".
   Two real faults were behind that:

   1. CONFIRM was always enabled. Press it before picking a point and the empty
      address was saved; press it while the address was still being looked up and
      the literal placeholder was saved. That is how a live business ended up
      with its address stored as the words "Loading address..." — confirmed in
      the production data.
   2. Nothing on the dialog told you whether a point had been chosen yet.

   Confirm is now disabled until a real address has resolved, the lookup state is
   visible, and every string is translatable. */
const MapModal = ({ open, handleClose, onLocationSelect, currentLocation }) => {
  const { t } = useTranslation();
  const [resolving, setResolving] = useState(false);
  /* [CF 2026-08-17 G2] "The map shows blank."
     The map was never broken — it was being pointed at nowhere. Businesses that
     have never had a location picked carry junk coordinates (12.001, 34.113),
     which is an empty patch of Sudan: Google returns a 178-byte blank tile there
     versus ~20KB of detail over Kuwait, so the dialog opened on featureless
     beige with a pin in the middle of it and no way to tell where you were.

     `parseFloat(x) || default` did not catch it, because 12.001 is a perfectly
     truthy number. Stored coordinates are now only trusted when the record also
     has a saved ADDRESS — i.e. someone really did pick a point before. Anything
     else opens over Kuwait, where the map has detail and the user can navigate. */
  const KUWAIT = { lat: 29.3759, lng: 47.9774 };

  const savedAddress = String(currentLocation?.address || "").trim();
  const savedLat = parseFloat(currentLocation?.lat);
  const savedLng = parseFloat(currentLocation?.long);
  const hasRealSavedPoint =
    !!savedAddress &&
    Number.isFinite(savedLat) &&
    Number.isFinite(savedLng) &&
    Math.abs(savedLat) <= 90 &&
    Math.abs(savedLng) <= 180 &&
    !(savedLat === 0 && savedLng === 0);

  const [selectedLocation, setSelectedLocation] = useState({
    lat: hasRealSavedPoint ? savedLat : KUWAIT.lat,
    lng: hasRealSavedPoint ? savedLng : KUWAIT.lng,
    address: hasRealSavedPoint ? savedAddress : "",
  });

  const searchInputRef = useRef(null);
  const mapRef = useRef(null);

  const mapContainerStyle = {
    height: "400px",
    width: "100%",
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;

    if (searchInputRef.current && window.google?.maps?.places) {
      const searchBox = new window.google.maps.places.SearchBox(
        searchInputRef.current
      );

      searchBox.addListener("places_changed", () => {
        const places = searchBox.getPlaces();
        if (places.length === 0) return;

        const place = places[0];
        if (!place.geometry || !place.geometry.location) return;

        setSelectedLocation({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          address: place.formatted_address || "Address not found",
        });

        mapRef.current.panTo(place.geometry.location);
      });
    }
  }, []);

  const onMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setSelectedLocation({ lat, lng, address: "" });
    setResolving(true);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      setResolving(false);
      if (status === "OK" && results[0]) {
        setSelectedLocation((prev) => ({
          ...prev,
          address: results[0].formatted_address,
        }));
      } else {
        // No street address here (sea, desert, unnamed plot). Keep the point —
        // the coordinates are what the map pin uses — and label it plainly.
        setSelectedLocation({ lat, lng, address: t("Address not found") });
      }
    });
  };

  const hasUsableAddress =
    !resolving && !!String(selectedLocation.address || "").trim();

  const handleSelect = () => {
    if (!hasUsableAddress) return;
    onLocationSelect({
      lat: selectedLocation.lat.toString(),
      long: selectedLocation.lng.toString(),
      address: selectedLocation.address,
    });
    handleClose();
  };

  const onMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setSelectedLocation((prev) => ({
      ...prev,
      lat,
      lng,
    }));
    setResolving(true);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      setResolving(false);
      if (status === "OK" && results[0]) {
        setSelectedLocation((prev) => ({
          ...prev,
          address: results[0].formatted_address,
        }));
      }
    });
  };

  const isGoogleMapsLoaded = window.google?.maps;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Fade in={open}>
        <div
          style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            width: "80%",
            maxWidth: "800px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <h3>{t('SELECT LOCATION ON MAP')}</h3>

          <input
            ref={searchInputRef}
            placeholder={t('Search for a location')}
            type="text"
            style={{
              margin: "10px 0",
              padding: "8px",
              boxSizing: "border-box",
              width: "100%",
            }}
          />

          {isGoogleMapsLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
              zoom={hasRealSavedPoint ? 15 : 11}
              onClick={onMapClick}
              onLoad={onMapLoad}
            >
              <Marker
                position={{
                  lat: selectedLocation.lat,
                  lng: selectedLocation.lng,
                }}
                draggable={true}
                onDragEnd={onMarkerDragEnd}
              />
            </GoogleMap>
          ) : (
            <div style={{ height: "400px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5", border: "1px solid #ddd", borderRadius: "4px" }}>
              <p>{t('Loading map...')}</p>
            </div>
          )}

          <div style={{ marginTop: "15px" }}>
            <p>
              <strong>{t('Selected Location:')}</strong>{' '}
              {resolving
                ? t('Looking up this address…')
                : selectedLocation.address || t('No address selected')}
            </p>
            <p>
              <strong>{t('Coordinates:')}</strong> {typeof selectedLocation.lat === 'number' ? selectedLocation.lat.toFixed(6) : selectedLocation.lat},{" "}
              {typeof selectedLocation.lng === 'number' ? selectedLocation.lng.toFixed(6) : selectedLocation.lng}
            </p>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "15px",
            }}
          >
            <Button
              onClick={handleClose}
              style={{ marginRight: "10px" }}
              variant="outlined"
            >
              {t('CANCEL')}
            </Button>
            <Button
              onClick={handleSelect}
              variant="contained"
              color="primary"
              disabled={!hasUsableAddress}
              title={
                hasUsableAddress ? undefined : t('Tap the map to choose a location first')
              }
            >
              {t('CONFIRM LOCATION')}
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default MapModal;
